import { InlineEditableField, TextTooltip } from '@components'
import { PermissionSet, usePermission } from '@context'
import { IAccountingSuggestion } from '@root/query/suggestions'
import { formatDate } from '@utils'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useId } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import { FaArrowsSplitUpAndLeft } from 'react-icons/fa6'
import { SuggestionCardFormValues } from '../types'
import { getDefaultAccrualDate } from '../utils'
import {
  AccountNameText,
  AccrualColumn,
  AccrualDateErrorText,
  AccrualDateInput,
  AccrualDateText,
  AccrualDateValue,
  AccrualGrid,
  AccrualZone,
  SplitHintBadge,
  SplitInfoBadge,
  TimelineConnector,
  TimelineConnectorWrap,
  TimelineDot,
  TimelineHeaderLeft,
  TimelineHeaderRight,
  TimelineHeaderRow,
  TimelineLabel,
  ValueText
} from '../styles'

const splitIconStyle: React.CSSProperties = {
  transform: 'rotateY(180deg)'
}

const splitButtonHoverAnimation = {
  scale: 1.05
}

const splitButtonTapAnimation = {
  scale: 0.97
}

const splitButtonSpringTransition = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 18,
  mass: 0.7
}

interface SuggestionCardAccrualSectionProps {
  suggestion: IAccountingSuggestion
}

const formatAccountLabel = (
  accountNumber?: string | number | null,
  accountName?: string | null
) => {
  const numberPart = accountNumber == null ? '' : String(accountNumber)
  const namePart = accountName ?? ''

  if (numberPart && namePart) return `${numberPart} ${namePart}`
  if (namePart) return namePart
  if (numberPart) return numberPart
  return '-'
}

export const SuggestionCardAccrualSection: React.FC<SuggestionCardAccrualSectionProps> = ({
  suggestion
}) => {
  const timelineId = useId()
  const accrualDotLayoutId = `${timelineId}-timeline-dot-accrual`
  const settlementDotLayoutId = `${timelineId}-timeline-dot-settlement`
  const hasEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const { t } = useTranslation()
  const canUseAccrualSplit = suggestion.accrual_split.enabled
  const { control, setValue, clearErrors, formState, getValues } =
    useFormContext<SuggestionCardFormValues>()
  const postingMode = useWatch({ control, name: 'posting_mode' })
  const accrualDate = useWatch({ control, name: 'accrual_date' })
  const isSplitMode = canUseAccrualSplit && postingMode === 'accrual_split'
  const accrualDateLabel = accrualDate || suggestion.accrual_split.suggested_date
  const hasAccrualDateError = Boolean(formState.errors.accrual_date)
  const isAccrualDateMissing = isSplitMode && !accrualDateLabel
  const accrualDateFieldState: 'default' | 'warning' | 'error' = hasAccrualDateError
    ? 'error'
    : isAccrualDateMissing
    ? 'warning'
    : 'default'

  const accrualAccountLabel = formatAccountLabel(
    suggestion.accrual_split.account_number,
    suggestion.accrual_split.account_name
  )
  const settlementAccountLabel = formatAccountLabel(
    suggestion.source_account?.number,
    suggestion.source_account?.name
  )

  const enableSplitMode = () => {
    if (!canUseAccrualSplit || !hasEditPermission) return
    setValue('posting_mode', 'accrual_split')
    setValue('relations', [])
    if (!getValues('accrual_date')) {
      setValue('accrual_date', getDefaultAccrualDate(suggestion))
    }
    clearErrors('accrual_date')
  }

  const disableSplitMode = () => {
    if (!canUseAccrualSplit || !hasEditPermission) return
    setValue('posting_mode', 'cash')
    clearErrors('accrual_date')
  }

  return (
    <AccrualZone>
      <AnimatePresence initial={false} mode="wait">
        {isSplitMode ? (
          <TimelineHeaderRow key="timeline-split" layout="position">
            <TimelineHeaderLeft as={motion.div}>
              <motion.div
                layoutId={accrualDotLayoutId}
                initial={{ opacity: 0, x: -8 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { delay: 0.14, duration: 0.2, ease: 'easeOut' }
                }}
                exit={{ opacity: 0, x: -8, transition: { duration: 0.12 } }}
              >
                <TimelineDot />
              </motion.div>
              <motion.div
                key="timeline-label-accrual"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.18 } }}
                exit={{ opacity: 0, x: -8, transition: { duration: 0.12 } }}
              >
                <TimelineLabel $tone="accrual">
                  {t('document.suggestionCard.timelineAccrual')}
                </TimelineLabel>
              </motion.div>
              {canUseAccrualSplit && hasEditPermission && (
                <>
                  <SplitHintBadge
                    type="button"
                    onClick={disableSplitMode}
                    whileHover={splitButtonHoverAnimation}
                    whileTap={splitButtonTapAnimation}
                    transition={splitButtonSpringTransition}
                  >
                    <span>{t('document.suggestionCard.unsplitAction')}</span>
                    <FaTimes />
                  </SplitHintBadge>
                  <TextTooltip
                    tooltip={t('document.suggestionCard.unsplitHint')}
                    placement="top-start"
                  >
                    <SplitInfoBadge
                      type="button"
                      aria-label={t('document.suggestionCard.splitHelpAria')}
                    >
                      ?
                    </SplitInfoBadge>
                  </TextTooltip>
                </>
              )}
            </TimelineHeaderLeft>

            <TimelineConnectorWrap
              initial={{ opacity: 0, x: -10 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: { delay: 0.14, duration: 0.2, ease: 'easeOut' }
              }}
              exit={{ opacity: 0, x: 10, transition: { duration: 0.12 } }}
            >
              <TimelineConnector />
            </TimelineConnectorWrap>

            <TimelineHeaderRight as={motion.div}>
              <motion.div
                key="timeline-label-settlement-right"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.18 } }}
                exit={{ opacity: 0, x: 8, transition: { duration: 0.12 } }}
              >
                <TimelineLabel $tone="settlement">
                  {t('document.suggestionCard.timelineSettlement')}
                </TimelineLabel>
              </motion.div>
              <motion.div layoutId={settlementDotLayoutId}>
                <TimelineDot $isRight />
              </motion.div>
            </TimelineHeaderRight>
          </TimelineHeaderRow>
        ) : canUseAccrualSplit ? (
          <TimelineHeaderRow key="timeline-cash" layout="position">
            <TimelineHeaderLeft as={motion.div}>
              <motion.div layoutId={settlementDotLayoutId}>
                <TimelineDot $isMuted />
              </motion.div>
              <motion.div
                key="timeline-label-settlement-left"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0, transition: { duration: 0.18 } }}
                exit={{ opacity: 0, x: 8, transition: { duration: 0.12 } }}
              >
                <TimelineLabel $tone="muted">
                  {t('document.suggestionCard.timelineSingle')}
                </TimelineLabel>
              </motion.div>
              {canUseAccrualSplit && hasEditPermission && (
                <>
                  <SplitHintBadge
                    type="button"
                    onClick={enableSplitMode}
                    whileHover={splitButtonHoverAnimation}
                    whileTap={splitButtonTapAnimation}
                    transition={splitButtonSpringTransition}
                  >
                    <span>{t('document.suggestionCard.splitAction')}</span>
                    <FaArrowsSplitUpAndLeft style={splitIconStyle} />
                  </SplitHintBadge>
                  <TextTooltip
                    tooltip={t('document.suggestionCard.splitHint')}
                    placement="top-start"
                  >
                    <SplitInfoBadge
                      type="button"
                      aria-label={t('document.suggestionCard.splitHelpAria')}
                    >
                      ?
                    </SplitInfoBadge>
                  </TextTooltip>
                </>
              )}
            </TimelineHeaderLeft>
          </TimelineHeaderRow>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode="wait">
        {isSplitMode ? (
          <AccrualGrid
            key="split-grid"
            layout="position"
            $isSplit={true}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.16 } }}
          >
            <AccrualColumn
              key="split-accrual"
              layout="position"
              initial={{ opacity: 0, x: -18 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: { delay: 0.12, duration: 0.22, ease: 'easeOut' }
              }}
              exit={{ opacity: 0, x: -14, transition: { duration: 0.16, ease: 'easeIn' } }}
            >
              <Controller
                name="accrual_date"
                control={control}
                rules={{
                  validate: value =>
                    postingMode !== 'accrual_split' || Boolean(value) || t('validation.required')
                }}
                render={({ field }) => (
                  <InlineEditableField
                    value={field.value}
                    onChange={value => {
                      field.onChange(value)
                      clearErrors('accrual_date')
                    }}
                    editable={hasEditPermission}
                    inputType="date"
                    displayValue={accrualDateLabel ? formatDate(accrualDateLabel) : '-'}
                    state={accrualDateFieldState}
                    minWidth={130}
                    title={t('document.suggestionCard.editAccrualDate')}
                    ValueComponent={AccrualDateValue}
                    InputComponent={AccrualDateInput}
                    TextComponent={AccrualDateText}
                  />
                )}
              />
              {hasAccrualDateError && (
                <AccrualDateErrorText>
                  {t('document.suggestionCard.accrualDateRequired')}
                </AccrualDateErrorText>
              )}
              <AccountNameText>{accrualAccountLabel}</AccountNameText>
            </AccrualColumn>

            <AccrualColumn
              key="split-settlement"
              $isSettlement
              layout="position"
              initial={{ x: -18, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { duration: 0.24, ease: 'easeOut' } }}
              exit={{
                x: -18,
                opacity: 0,
                transition: { delay: 0.16, duration: 0.2, ease: 'easeInOut' }
              }}
            >
              <ValueText>{formatDate(suggestion.date)}</ValueText>
              <AccountNameText>{settlementAccountLabel}</AccountNameText>
            </AccrualColumn>
          </AccrualGrid>
        ) : canUseAccrualSplit ? (
          <AccrualGrid
            key="cash-grid"
            layout="position"
            $isSplit={false}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.16 } }}
          >
            <AccrualColumn
              key="cash-settlement"
              layout="position"
              initial={{ x: 18, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { duration: 0.24, ease: 'easeOut' } }}
              exit={{ x: 0, opacity: 0, transition: { duration: 0.16 } }}
            >
              <ValueText $emphasis={false}>{formatDate(suggestion.date)}</ValueText>
              <AccountNameText>{settlementAccountLabel}</AccountNameText>
            </AccrualColumn>
          </AccrualGrid>
        ) : null}
      </AnimatePresence>
    </AccrualZone>
  )
}
